import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
// FIREBASE REMOVED: All Firebase imports removed
import { useAuth } from './AuthProvider';
import { sendSafeNotification } from './utils/safeNotifications';
import { notificationAPI } from './services/apiClient';

export interface Notification {
  id: string;
  baslik?: string;
  icerik?: string;
  mesaj?: string;
  tur?: string;
  oncelik?: string;
  url?: string | null;
  servisId?: string | null;
  okundu?: boolean;
  tarih?: any;
  olusturan?: string;
  olusturanAdi?: string;
  hedefKullanici?: string | null;
  hedefRol?: string | null;
  [key: string]: any;
}

export interface NotificationData {
  title?: string;
  content?: string;
  message?: string;
  type?: string;
  priority?: string;
  url?: string;
  servisId?: string;
  targetUser?: string;
  targetRole?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isConnected: boolean;
  permission: NotificationPermission;
  requestPermission: () => Promise<NotificationPermission>;
  sendNotification: (data: NotificationData) => Promise<boolean>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  getUnreadNotifications: () => Notification[];
}

export const NOTIFICATION_TYPES = {
  INFO: 'info',
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  SERVICE: 'service',
  STOCK: 'stock',
  CUSTOMER: 'customer',
  PAYMENT: 'payment',
  SYSTEM: 'system'
};

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps): React.ReactElement {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isConnected, setIsConnected] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [permission, setPermission] = useState<NotificationPermission>(typeof Notification !== 'undefined' ? Notification.permission : 'default');

  // Show browser notification
  const showBrowserNotification = useCallback((notification: Notification): void => {
    if (permission !== 'granted') return;
    
    const success = sendSafeNotification(notification.baslik || 'Yeni Bildirim', {
      body: notification.icerik || notification.mesaj || 'Yeni bir bildiriminiz var',
      tag: notification.id,
      requireInteraction: false,
      silent: false,
      vibrate: [100, 50, 100],
      data: {
        notificationId: notification.id,
        url: notification.url || '/'
      }
    });

    if (success) {
      console.log('[NotificationProvider] Browser notification shown:', notification.baslik);
    }
  }, [permission]);

  // Real-time notification listener - Optimized polling (SSE Apache ile sorunlu)
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    let lastCheckTime = new Date().toISOString();
    let pollingInterval: NodeJS.Timeout | null = null;
    let isPageVisible = true;

    // İlk yüklemede tüm bildirimleri çek
    const loadInitialNotifications = async () => {
      try {
        const response = await notificationAPI.getAll({ limit: 50 });
        if (response.data?.success) {
          const notificationsData = response.data.data?.notifications || response.data.data || [];
          const formattedNotifications = notificationsData.map((notif: any) => ({
            id: notif.id,
            baslik: notif.title,
            icerik: notif.message,
            mesaj: notif.message,
            tur: notif.type || 'info',
            oncelik: notif.priority || 'normal',
            url: notif.url || null,
            servisId: notif.reference_type === 'service' ? notif.reference_id : null,
            okundu: notif.is_read || false,
            tarih: notif.created_at,
            olusturan: notif.user_id,
            olusturanAdi: notif.user?.display_name || notif.user?.email || null
          }));
          
          setNotifications(formattedNotifications);
          setUnreadCount(formattedNotifications.filter((n: Notification) => !n.okundu).length);
          
          // Son bildirim zamanını güncelle
          if (formattedNotifications.length > 0) {
            lastCheckTime = formattedNotifications[0].tarih || new Date().toISOString();
          }
        }
      } catch (error: any) {
        // Error handling - log removed for performance
        setNotifications([]);
        setUnreadCount(0);
      }
    };

    // Yeni bildirimleri kontrol et ve yükle
    const checkAndLoadNewNotifications = async () => {
      if (!isPageVisible || !isConnected || !user) return;

      try {
        // Önce hafif kontrol - yeni var mı?
        const checkResponse = await notificationAPI.checkNew({ last_check_time: lastCheckTime });
        
        if (checkResponse.data?.success && checkResponse.data.data?.hasNew) {
          // Yeni bildirim var, sadece yeni olanları çek
          const newResponse = await notificationAPI.getNew({ last_check_time: lastCheckTime, limit: 50 });
          
          if (newResponse.data?.success) {
            const newNotificationsData = newResponse.data.data?.notifications || [];
            const formattedNewNotifications = newNotificationsData.map((notif: any) => ({
              id: notif.id,
              baslik: notif.title,
              icerik: notif.message,
              mesaj: notif.message,
              tur: notif.type || 'info',
              oncelik: notif.priority || 'normal',
              url: notif.url || null,
              servisId: notif.reference_type === 'service' ? notif.reference_id : null,
              okundu: notif.is_read || false,
              tarih: notif.created_at,
              olusturan: notif.user_id,
              olusturanAdi: notif.user?.display_name || notif.user?.email || null
            }));

            // Sadece yeni bildirimleri state'e ekle
            setNotifications((prevNotifications) => {
              const existingIds = new Set(prevNotifications.map((n: Notification) => n.id));
              const trulyNew = formattedNewNotifications.filter((n: Notification) => !existingIds.has(n.id));
              
              // Yeni bildirimler varsa browser notification göster
              trulyNew.forEach((notification: Notification) => {
                if (!notification.okundu && permission === 'granted') {
                  showBrowserNotification(notification);
                }
              });
              
              // Yeni bildirimleri başa ekle
              const updatedNotifications = [...trulyNew, ...prevNotifications].slice(0, 100);
              
              // Unread count'u güncelle - backend'den gelen değeri kullan (en güncel)
              const backendUnreadCount = newResponse.data.data?.unreadCount;
              if (backendUnreadCount !== undefined && backendUnreadCount !== null) {
                setUnreadCount(backendUnreadCount);
              } else {
                // Backend'den gelmediyse, güncellenmiş listeden hesapla
                const calculatedUnreadCount = updatedNotifications.filter((n: Notification) => !n.okundu).length;
                setUnreadCount(calculatedUnreadCount);
              }
              
              return updatedNotifications;
            });
            
            // Son kontrol zamanını güncelle
            if (newResponse.data.data?.currentTime) {
              lastCheckTime = newResponse.data.data.currentTime;
            } else if (formattedNewNotifications.length > 0) {
              lastCheckTime = formattedNewNotifications[0].tarih || new Date().toISOString();
            }
          }
        } else if (checkResponse.data?.success) {
          // Yeni bildirim yok ama unread count'u güncelle (başka bir yerde okunmuş olabilir)
          const currentUnreadCount = checkResponse.data.data?.unreadCount;
          if (currentUnreadCount !== undefined) {
            setUnreadCount(currentUnreadCount);
          }
          
          // Son kontrol zamanını güncelle
          if (checkResponse.data.data?.currentTime) {
            lastCheckTime = checkResponse.data.data.currentTime;
          }
        }
      } catch (error: any) {
        // 503 (Service Unavailable) gibi geçici hataları sessizce handle et
        const status = error?.response?.status;
        if (status === 503 || status === 502 || status === 504) {
          // Geçici server hataları - sessizce devam et, bir sonraki polling'de tekrar dene
          return;
        }
        // Error handling - log removed for performance
      }
    };

    // İlk yükleme
    loadInitialNotifications();

    // Page visibility API - sayfa görünür değilse polling'i durdur
    const handleVisibilityChange = () => {
      isPageVisible = !document.hidden;
      if (isPageVisible && user && isConnected) {
        // Sayfa tekrar görünür olduğunda hemen kontrol et
        checkAndLoadNewNotifications();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Optimized polling - Her 10 saniyede bir kontrol (SSE yerine)
    pollingInterval = setInterval(() => {
      if (user && isConnected && isPageVisible) {
        checkAndLoadNewNotifications();
      }
    }, 10000); // 10 saniye (SSE'den daha sık ama trafik optimize)

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, permission, isConnected, showBrowserNotification]);

  // Network status listener
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const handleOnline = () => setIsConnected(true);
    const handleOffline = () => setIsConnected(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      const permission = await Notification.requestPermission();
      setPermission(permission);
      return permission;
    }
    return 'denied';
  }, []);

  // Send notification
  const sendNotification = useCallback(async (notificationData: NotificationData): Promise<boolean> => {
    try {
      if (!user) return false;

      // Backend'e bildirim gönder
      const response = await notificationAPI.create({
        title: notificationData.title || 'Bildirim',
        message: notificationData.content || notificationData.message || '',
        type: notificationData.type || 'info',
        priority: notificationData.priority || 'normal',
        url: notificationData.url || undefined,
        reference_id: notificationData.servisId || undefined,
        reference_type: notificationData.servisId ? 'service' : undefined,
        target_user_id: notificationData.targetUser || undefined
      });

      if (response.data?.success) {
        const backendNotification = response.data.data;
        
        // Frontend formatına dönüştür
        const notification = {
          id: backendNotification.id,
          baslik: backendNotification.title,
          icerik: backendNotification.message,
          mesaj: backendNotification.message,
          tur: backendNotification.type || 'info',
          oncelik: backendNotification.priority || 'normal',
          url: backendNotification.url || null,
          servisId: backendNotification.reference_type === 'service' ? backendNotification.reference_id : null,
          okundu: false,
          tarih: backendNotification.created_at || new Date(),
          olusturan: user.id,
          olusturanAdi: user.display_name || user.email
        };

        // Local state'e ekle
        setNotifications(prev => [notification, ...prev]);
        setUnreadCount(prev => prev + 1);

        // Show browser notification if permission granted
        if (permission === 'granted') {
          showBrowserNotification(notification as Notification);
        }

        return true;
      }

      return false;
    } catch (error: any) {
      // Error handling - log removed for performance
      return false;
    }
  }, [user, permission, showBrowserNotification]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string): Promise<void> => {
    try {
      if (!user) return;

      // Backend API'ye bildirim okundu işaretleme
      await notificationAPI.markAsRead(notificationId);
      
      // Local state'i güncelle
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId ? { ...notif, okundu: true } : notif
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error: any) {
      // Error handling - log removed for performance
    }
  }, [user]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async (): Promise<void> => {
    try {
      if (!user) return;

      // Backend API'ye tüm bildirimleri okundu işaretleme
      await notificationAPI.markAllAsRead();
      
      // Local state'i güncelle
      setNotifications(prev => prev.map(notif => ({ ...notif, okundu: true })));
      setUnreadCount(0);
    } catch (error: any) {
      // Error handling - log removed for performance
    }
  }, [user]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string): Promise<void> => {
    try {
      if (!user) return;

      // Backend API'ye bildirim silme
      await notificationAPI.delete(notificationId);
      
      // Local state'i güncelle
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      setUnreadCount(prev => {
        const deleted = notifications.find(n => n.id === notificationId);
        return deleted && !deleted.okundu ? Math.max(0, prev - 1) : prev;
      });
    } catch (error: any) {
      // Error handling - log removed for performance
    }
  }, [user, notifications]);

  // Clear all notifications
  const clearAllNotifications = useCallback(async (): Promise<void> => {
    try {
      if (!user) return;

      // Backend API'ye tüm bildirimleri silme
      await notificationAPI.clearAll();
      
      // Local state'i temizle
      setNotifications([]);
      setUnreadCount(0);
    } catch (error: any) {
      // Error handling - log removed for performance
    }
  }, [user]);

  // Get unread notifications
  const getUnreadNotifications = useCallback((): Notification[] => {
    return notifications.filter(notif => !notif.okundu);
  }, [notifications]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isConnected,
    permission,
    requestPermission,
    sendNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    getUnreadNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextType {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
}

// Notification priorities
export const NOTIFICATION_PRIORITIES = {
  LOW: 'low',
  NORMAL: 'normal',
  HIGH: 'high',
  URGENT: 'urgent'
};

export default NotificationContext;
